import React, { useEffect, useRef, useState } from 'react';
import { verifySecondFactor, ENROLMENT_PENDING_MESSAGE } from '../auth/loginResponse';
import { INPUT, PRIMARY, LINK } from '../auth/mfaStyles';

// The code step of sign-in. Holds the step-up token only in props/state; never in storage.
export default function MfaCodeStep({ mfaToken, apiUrl = '', onVerified, onBack, onExpired }) {
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, [useRecovery, error]);

  const submit = async (e) => {
    e.preventDefault();
    const entered = value.trim();
    if (!entered || loading) return;
    setLoading(true);
    setError('');
    const result = await verifySecondFactor(apiUrl, mfaToken, useRecovery ? { recoveryCode: entered } : { code: entered });
    if (result.ok) {
      try {
        await onVerified(result.data);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
      return;
    }
    setLoading(false);
    if (result.reason === 'expired') { onExpired(result.message); return; }
    setValue('');
    setError(result.message);
  };

  const toggle = () => { setUseRecovery((on) => !on); setValue(''); setError(''); };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="mfa-code-form" noValidate>
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Two-factor sign-in</h2>
        <p className="text-sm text-gray-600 mt-1">
          {useRecovery ? 'Enter one of your saved recovery codes.' : 'Enter the 6-digit code from your authenticator app.'}
        </p>
      </div>
      <div role="alert" aria-live="assertive" data-testid="mfa-error"
        className={error ? 'bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm' : 'sr-only'}>{error}</div>
      <div>
        <label htmlFor="mfa-code-input" className="block text-sm font-medium text-gray-700 mb-2">
          {useRecovery ? 'Recovery code' : 'Authentication code'}
        </label>
        <input id="mfa-code-input" data-testid="mfa-code-input" ref={inputRef} className={INPUT}
          type="text" value={value} onChange={(e) => setValue(e.target.value)} required
          {...(useRecovery
            ? { inputMode: 'text', autoComplete: 'off', autoCapitalize: 'none', spellCheck: false, maxLength: 64 }
            : { inputMode: 'numeric', autoComplete: 'one-time-code', pattern: '[0-9]*', maxLength: 6 })} />
      </div>
      <button type="submit" disabled={loading} className={PRIMARY} data-testid="mfa-verify-btn">
        {loading ? 'Verifying…' : 'Verify'}
      </button>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={toggle} className={LINK} data-testid="mfa-toggle-recovery">
          {useRecovery ? 'Use an authenticator code instead' : 'Use a recovery code instead'}
        </button>
        <button type="button" onClick={onBack} className={LINK} data-testid="mfa-back-btn">Back to sign in</button>
      </div>
    </form>
  );
}

// Holding screen until forced enrolment ships: shown when the organisation requires two-factor and the user has none.
export function MfaEnrolmentPending({ onBack }) {
  return (
    <div className="space-y-5" data-testid="mfa-enrolment-pending">
      <p role="status" className="text-sm text-gray-700">{ENROLMENT_PENDING_MESSAGE}</p>
      <button type="button" onClick={onBack} className={PRIMARY} data-testid="mfa-enrolment-back-btn">Back</button>
    </div>
  );
}
